<template>
  <div class="wh">
    <!-- ═══ header: brand · branch · provider · tabs ═══ -->
    <div class="wh-bar">
      <div class="wh-brand">
        <span class="wh-brand__badge"><i class="fab fa-whatsapp"/></span>
        <div class="wh-brand__text">
          <span class="wh-brand__title">{{ $t('whatsappHub.title') }}</span>
          <span v-if="status" class="wh-brand__sub">
            <span class="wh-provider" :data-provider="status.provider">
              <i/>{{ status.provider === 'TWILIO' ? 'Twilio' : status.provider === 'META' ? 'Meta' : $t('whatsappHub.provider.none') }}
            </span>
            <span v-if="status.sender" class="tabular">{{ status.sender }}</span>
          </span>
        </div>
      </div>

      <v-select v-if="branchOptions.length > 1" v-model="buid" :options="branchOptions" :reduce="o => o.value" label="text" :clearable="false"
                :dir="isRTL ? 'rtl' : 'ltr'" class="wh-branch" append-to-body/>
      <span v-else-if="branchOptions.length === 1" class="wh-branch-name">{{ branchOptions[0].text }}</span>

      <nav class="wh-tabs">
        <button v-for="t in tabs" :key="t.key" type="button" class="wh-tab" :class="{'wh-tab--on': tab === t.key}" @click="tab = t.key">
          <i :class="t.icon"/>{{ $t('whatsappHub.tabs.' + t.key) }}
          <span v-if="t.badge" class="wh-tab__badge tabular">{{ t.badge }}</span>
        </button>
      </nav>

      <span class="wh-bar__spacer"/>

      <button v-if="status && status.inboundWebhookUrl" type="button" class="wh-tool" v-b-tooltip.hover :title="$t('whatsappHub.webhooks.title')"
              @click="webhooksDialog = true">
        <i class="fas fa-plug"/>
      </button>
      <b-modal v-model="webhooksDialog" centered hide-footer size="md" content-class="wh-webhooks" body-class="p-0">
        <template #modal-title>
          <span class="wh-webhooks__title"><i class="fas fa-plug"/>{{ $t('whatsappHub.webhooks.title') }}</span>
        </template>
        <div v-if="status" class="wh-webhooks__body">
          <!-- one click: the CRM sets the URLs on the Twilio sender itself -->
          <div class="wh-webhooks__auto" :data-state="webhookState">
            <div class="wh-webhooks__auto-head">
              <span class="wh-webhooks__auto-icon">
                <i :class="webhookState === 'ok' ? 'fas fa-check' : webhookState === 'missing' ? 'fas fa-link-slash' : 'fas fa-question'"/>
              </span>
              <div class="wh-webhooks__auto-text">
                <b>{{ $t('whatsappHub.webhooks.state.' + webhookState) }}</b>
                <small v-if="webhookState === 'ok'">{{ $t('whatsappHub.webhooks.okHint', {number: status.sender}) }}</small>
                <small v-else-if="webhookState === 'missing' && status.registeredInboundUrl">{{ $t('whatsappHub.webhooks.currently') }} <code>{{ status.registeredInboundUrl }}</code></small>
                <small v-else-if="webhookState === 'missing'">{{ $t('whatsappHub.webhooks.registerHint', {number: status.sender}) }}</small>
                <small v-else>{{ $t('whatsappHub.webhooks.unknownHint') }}</small>
              </div>
            </div>
            <b-button v-if="webhookState !== 'ok'" block variant="success" class="wh-webhooks__auto-btn" :disabled="registering" @click="registerWebhooks">
              <b-spinner v-if="registering" small class="mr-50"/><i v-else class="fas fa-bolt mr-50"/>
              {{ registering ? $t('whatsappHub.webhooks.registering') : $t('whatsappHub.webhooks.register') }}
            </b-button>
            <button v-else type="button" class="wh-webhooks__again" :disabled="registering" @click="registerWebhooks">
              <b-spinner v-if="registering" small class="mr-25"/><i v-else class="fas fa-rotate mr-25"/>{{ $t('whatsappHub.webhooks.reregister') }}
            </button>
          </div>
          <p class="wh-webhooks__hint">{{ $t('whatsappHub.webhooks.hint') }}</p>
          <div class="wh-webhooks__item">
            <span class="wh-webhooks__step">1</span>
            <div class="wh-webhooks__main">
              <span class="wh-webhooks__label">{{ $t('whatsappHub.webhooks.inbound') }}</span>
              <span class="wh-webhooks__method">POST</span>
              <div class="wh-webhooks__url">
                <code>{{ status.inboundWebhookUrl }}</code>
                <button type="button" v-b-tooltip.hover :title="$t('whatsappHub.webhooks.copy')" @click="copy(status.inboundWebhookUrl)"><i class="far fa-copy"/></button>
              </div>
            </div>
          </div>
          <div class="wh-webhooks__item">
            <span class="wh-webhooks__step">2</span>
            <div class="wh-webhooks__main">
              <span class="wh-webhooks__label">{{ $t('whatsappHub.webhooks.status') }}</span>
              <span class="wh-webhooks__method">POST</span>
              <div class="wh-webhooks__url">
                <code>{{ status.statusWebhookUrl }}</code>
                <button type="button" v-b-tooltip.hover :title="$t('whatsappHub.webhooks.copy')" @click="copy(status.statusWebhookUrl)"><i class="far fa-copy"/></button>
              </div>
              <small class="wh-webhooks__note">{{ $t('whatsappHub.webhooks.statusNote') }}</small>
            </div>
          </div>
          <div class="wh-webhooks__where">
            <b>{{ $t('whatsappHub.webhooks.manualTitle') }}</b>
            <span><i class="fas fa-location-arrow"/>{{ $t('whatsappHub.webhooks.where') }}</span>
            <span><i class="fas fa-location-arrow"/>{{ $t('whatsappHub.webhooks.whereService') }}</span>
            <span><i class="fas fa-location-arrow"/>{{ $t('whatsappHub.webhooks.whereSandbox') }}</span>
          </div>
        </div>
      </b-modal>
      <button v-if="status && status.configured" type="button" class="wh-tool" v-b-tooltip.hover :title="$t('whatsappHub.settings.title')" @click="openSettings">
        <i class="fas fa-gear"/>
      </button>
      <b-modal v-model="settingsDialog" centered hide-footer size="md" body-class="p-0">
        <template #modal-title>
          <span class="wh-webhooks__title"><i class="fas fa-gear"/>{{ $t('whatsappHub.settings.title') }}</span>
        </template>
        <div class="wh-settings">
          <div class="wh-settings__row">
            <div class="wh-settings__text">
              <b>{{ $t('whatsappHub.settings.autoReply') }}</b>
              <small>{{ $t('whatsappHub.settings.autoReplyHint') }}</small>
            </div>
            <b-form-checkbox v-model="settings.autoReply" switch size="lg" class="wh-settings__switch"/>
          </div>
          <div v-if="settings.autoReply" class="wh-settings__block">
            <label class="wh-settings__label">{{ $t('whatsappHub.settings.autoReplyText') }}
              <b-dropdown size="sm" variant="outline-secondary" right boundary="window" class="ml-auto">
                <template #button-content><i class="fas fa-tag mr-25"/>{{ $t('whatsappHub.composer.tagInsert') }}</template>
                <b-dropdown-item v-for="tag in autoReplyTags" :key="tag" @click="settings.autoReplyText = (settings.autoReplyText || settings.defaultAutoReplyText || '') + '#' + tag + '#'">
                  <code>#{{ tag }}#</code>
                </b-dropdown-item>
              </b-dropdown>
            </label>
            <b-form-textarea v-model="settings.autoReplyText" rows="5" :placeholder="settings.defaultAutoReplyText"/>
            <small class="wh-settings__hint">{{ $t('whatsappHub.settings.autoReplyTextHint') }}</small>
          </div>
          <div class="wh-settings__foot">
            <b-button variant="flat-secondary" @click="settingsDialog = false">{{ $t('whatsappHub.composer.close') }}</b-button>
            <b-button variant="success" :disabled="savingSettings" @click="saveSettings">
              <b-spinner v-if="savingSettings" small class="mr-50"/><i v-else class="fas fa-check mr-50"/>{{ $t('whatsappHub.settings.save') }}
            </b-button>
          </div>
        </div>
      </b-modal>
      <button type="button" class="wh-tool" v-b-tooltip.hover :title="$t('whatsappHub.reload')" @click="refreshAll">
        <i class="fas fa-redo" :class="{'fa-spin': loadingStatus}"/>
      </button>
    </div>

    <!-- not configured -->
    <b-alert v-if="status && !status.configured" show variant="warning" class="wh-alert">
      <i class="fas fa-triangle-exclamation mr-50"/>{{ status.hint }}
      <router-link :to="'/organization/branches'" class="ml-50 font-weight-bold">{{ $t('whatsappHub.openBranchSettings') }} →</router-link>
    </b-alert>
    <b-alert v-else-if="status && status.provider === 'META'" show variant="secondary" class="wh-alert">
      <i class="fas fa-circle-info mr-50"/>{{ status.hint }}
    </b-alert>

    <!-- template picked in the gallery: pick guests next -->
    <transition name="wh-fade">
      <div v-if="pendingTemplate && tab === 'audience'" class="wh-pending">
        <i class="fas fa-file-lines"/>
        <span>{{ $t('whatsappHub.pendingTemplate', {name: pendingTemplate.name}) }}</span>
        <span class="wh-bar__spacer"/>
        <button type="button" class="wh-pending__x" @click="pendingTemplate = null"><i class="fas fa-times"/></button>
      </div>
    </transition>

    <!-- ═══ panels (kept alive so filters and selections survive tab switches) ═══ -->
    <div class="wh-panel">
      <whatsapp-audience-table v-show="tab === 'audience'" ref="audience" :buid="buid" :status="status"
                               @compose="compose" @open-chat="openChat" @open-contract="openContract"/>
      <whatsapp-conversations v-show="tab === 'chats'" ref="chats" :buid="buid"
                              @open-chat="openChat" @open-contract="openContract" @compose="compose" @loaded="unread = $event.unread"/>
      <whatsapp-template-gallery v-show="tab === 'templates'" ref="templates" :buid="buid" :status="status" @use="useTemplate"/>
      <whatsapp-scheduled-list v-show="tab === 'scheduled'" ref="scheduled" :buid="buid" @loaded="pendingScheduled = $event.pending"/>
    </div>

    <whatsapp-composer ref="composer" :buid="buid" :status="status" @sent="onSent" @closed="onComposerClosed"/>
    <whatsapp-thread ref="thread" :buid="buid" :status="status" @compose-template="row => compose([row], {mode: 'TEMPLATE'})"
                     @open-contract="openContract" @seen="onSeen" @sent="onThreadSent"/>
    <contract-rent-sidebar ref="contractSidebar"/>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import ContractRentSidebar from '@/components/sidebar/ContractRent/ContractRentSidebar.vue'
import WhatsappAudienceTable from '@/components/panels/whatsapp/WhatsappAudienceTable.vue'
import WhatsappConversations from '@/components/panels/whatsapp/WhatsappConversations.vue'
import WhatsappTemplateGallery from '@/components/panels/whatsapp/WhatsappTemplateGallery.vue'
import WhatsappScheduledList from '@/components/panels/whatsapp/WhatsappScheduledList.vue'
import WhatsappComposer from '@/components/panels/whatsapp/WhatsappComposer.vue'
import WhatsappThread from '@/components/panels/whatsapp/WhatsappThread.vue'

const STORAGE_KEY = 'whatsappHub.buid';

/**
 * The WhatsApp hub — one branch at a time (Twilio credentials and templates
 * are per branch): reservations as an audience, bulk/scheduled sends,
 * conversations with delivery ticks, and the template library + builder.
 */
export default {
  name: 'WhatsappHub',
  components: {
    ContractRentSidebar, WhatsappAudienceTable, WhatsappConversations, WhatsappTemplateGallery,
    WhatsappScheduledList, WhatsappComposer, WhatsappThread,
  },
  data: () => ({
    buid: null,
    tab: 'audience',
    status: null,
    loadingStatus: false,
    unread: 0,
    pendingScheduled: 0,
    pendingTemplate: null,
    webhooksDialog: false,
    registering: false,
    reopenThread: null,
    settingsDialog: false,
    savingSettings: false,
    ticker: null,
    settings: {autoReply: true, autoReplyText: '', defaultAutoReplyText: ''},
    autoReplyTags: ['firstname', 'lastname', 'fullname', 'propertyAddress', 'ownerCompany', 'branchPhone', 'branchEmail'],
    listeners: {branch: () => {}},
  }),
  computed: {
    ...mapGetters('data', ['getSelectedBranchesIds', 'getAllBranches']),
    branchOptions() {
      const all = this.getAllBranches || {};
      const selected = this.getSelectedBranchesIds || [];
      return Object.values(all)
          .filter(b => selected.length === 0 || selected.includes(b.id))
          .map(b => ({value: b.id, text: b.name}));
    },
    webhookState() {
      if (!this.status) return 'unknown';
      return this.status.webhookRegistered === true ? 'ok' : this.status.webhookRegistered === false ? 'missing' : 'unknown';
    },
    tabs() {
      return [
        {key: 'audience', icon: 'fas fa-users'},
        {key: 'chats', icon: 'far fa-comments', badge: this.unread || null},
        {key: 'templates', icon: 'fas fa-file-lines'},
        {key: 'scheduled', icon: 'far fa-clock', badge: this.pendingScheduled || null},
      ];
    },
  },
  watch: {
    buid(value) {
      try { localStorage.setItem(STORAGE_KEY, String(value)); } catch (e) { /* private mode */ }
      this.pendingTemplate = null;
      this.loadStatus();
    },
    branchOptions: {
      handler() {
        this.pickBranch();
      },
      immediate: true,
    },
  },
  created() {
    this.$root.$on('crm::branch-updated', this.listeners.branch = () => this.pickBranch());
    // live feel: the visible list refreshes itself while nothing is being edited
    this.ticker = setInterval(() => this.tick(), 20000);
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.listeners.branch);
    clearInterval(this.ticker);
  },
  methods: {
    ...mapActions('whatsapp', ['hubStatus', 'hubRegisterWebhooks', 'hubSettings', 'hubSaveSettings', 'refreshUnread']),
    tick() {
      if (document.hidden || !this.buid) return;
      const composerOpen = this.$refs.composer && this.$refs.composer.visible;
      if (composerOpen) return;
      if (this.tab === 'audience' && this.$refs.audience && this.$refs.audience.selectedRows.length === 0) this.$refs.audience.refresh();
      else if (this.tab === 'chats' && this.$refs.chats) this.$refs.chats.refresh();
      else if (this.tab === 'scheduled' && this.$refs.scheduled) this.$refs.scheduled.refresh();
      this.refreshUnread();
    },
    openSettings() {
      this.settingsDialog = true;
      this.hubSettings({buid: this.buid}).then(s => this.settings = Object.assign({autoReply: true, autoReplyText: '', defaultAutoReplyText: ''}, s)).catch(error => {
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      });
    },
    saveSettings() {
      this.savingSettings = true;
      this.hubSaveSettings({buid: this.buid, autoReply: this.settings.autoReply, autoReplyText: this.settings.autoReplyText})
          .then(s => { if (s) this.settings = Object.assign({}, this.settings, s); this.settingsDialog = false; })
          .catch(() => {}).finally(() => this.savingSettings = false);
    },
    registerWebhooks() {
      this.registering = true;
      this.hubRegisterWebhooks({buid: this.buid}).then(status => this.status = status).catch(() => {
      }).finally(() => this.registering = false);
    },
    pickBranch() {
      const options = this.branchOptions;
      if (options.length === 0) return;
      if (this.buid != null && options.some(o => o.value === this.buid)) return;
      let stored = null;
      try { stored = Number(localStorage.getItem(STORAGE_KEY)); } catch (e) { /* private mode */ }
      const found = options.find(o => o.value === stored);
      this.buid = found ? found.value : options[0].value;
    },
    loadStatus() {
      if (!this.buid) return;
      this.loadingStatus = true;
      this.hubStatus({buid: this.buid}).then(status => this.status = status).catch(error => {
        this.status = null;
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      }).finally(() => this.loadingStatus = false);
    },
    refreshAll() {
      this.loadStatus();
      ['audience', 'chats', 'scheduled'].forEach(ref => this.$refs[ref] && this.$refs[ref].refresh());
      if (this.$refs.templates) this.$refs.templates.refresh(true);
    },

    compose(rows, options) {
      const opts = Object.assign({}, options || {});
      if (this.pendingTemplate && (!opts.mode || opts.mode === 'TEMPLATE')) {
        opts.template = this.pendingTemplate;
        opts.mode = 'TEMPLATE';
      }
      // the drawer and the composer would sit on top of each other — park the drawer and bring it back afterwards
      const thread = this.$refs.thread;
      if (thread && thread.visible) {
        this.reopenThread = thread.recipientRow;
        thread.close();
      } else {
        this.reopenThread = null;
      }
      this.$refs.composer.open(rows, opts);
    },
    onComposerClosed() {
      if (this.reopenThread) {
        const row = this.reopenThread;
        this.reopenThread = null;
        this.$nextTick(() => this.$refs.thread.open(row));
      }
    },
    useTemplate(tpl) {
      this.pendingTemplate = tpl;
      this.tab = 'audience';
    },
    openChat(row) {
      this.$refs.thread.open(row);
    },
    openContract(contractId) {
      if (contractId) this.$refs.contractSidebar.showContract(contractId);
    },
    onSent(results) {
      this.pendingTemplate = null;
      if (this.$refs.audience) this.$refs.audience.refresh();
      if (this.$refs.chats) this.$refs.chats.refresh();
      if (results.some(r => r.scheduled) && this.$refs.scheduled) this.$refs.scheduled.refresh();
    },
    onThreadSent() {
      if (this.$refs.audience) this.$refs.audience.refresh();
      if (this.$refs.chats) this.$refs.chats.refresh();
    },
    onSeen(clientId) {
      this.refreshUnread();
      if (this.$refs.chats) this.$refs.chats.markSeenLocally(clientId);
      if (this.$refs.audience) {
        this.$refs.audience.rows.forEach(r => { if (r.clientId === clientId) r.unread = false; });
      }
    },
    copy(text) {
      if (navigator.clipboard) navigator.clipboard.writeText(text);
      this.$store.dispatch('notifications/success', {title: {text: 'WhatsApp'}, message: {text: '%s copied', arguments: ['URL']}});
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wh { display: flex; flex-direction: column; gap: 12px; }

.wh-bar {
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
  background: #fff; border: 1px solid rgba(15, 23, 42, .06); border-radius: 16px; padding: 12px 16px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, .04);
}
.wh-bar__spacer { flex: 1; }
.wh-brand { display: flex; align-items: center; gap: 10px; }
.wh-brand__badge {
  width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #25d366, #128c7e); color: #fff; font-size: 1.3rem; box-shadow: 0 6px 14px rgba(37, 211, 102, .3);
}
.wh-brand__text { display: flex; flex-direction: column; }
.wh-brand__title { font-size: 1rem; font-weight: 800; color: #1f2a44; line-height: 1.1; }
.wh-brand__sub { display: inline-flex; align-items: center; gap: 8px; font-size: .7rem; color: #94a3b8; margin-top: 2px; }
.wh-provider {
  display: inline-flex; align-items: center; gap: 5px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; font-size: .6rem;
  i { width: 6px; height: 6px; border-radius: 50%; background: #cbd5e1; }
  &[data-provider="TWILIO"] { color: #e11d48; i { background: #f22f46; } }
  &[data-provider="META"] { color: #1877f2; i { background: #1877f2; } }
  &[data-provider="NONE"] { color: #ea5455; i { background: #ea5455; } }
}
.wh-branch { min-width: 220px; }
.wh-branch-name { font-size: .84rem; font-weight: 700; color: #475569; }
.wh-tabs { display: inline-flex; background: rgba(15, 23, 42, .04); border-radius: 12px; padding: 4px; gap: 2px; }
.wh-tab {
  display: inline-flex; align-items: center; gap: 6px; border: none; border-radius: 9px; background: transparent; cursor: pointer;
  padding: 7px 14px; font-size: .76rem; font-weight: 800; color: #64748b; transition: all .15s;
  i { font-size: .74rem; }
  &:hover { color: #1f2a44; }
  &:focus { outline: none; }
  &--on { background: #fff; color: #1f9d57; box-shadow: 0 2px 6px rgba(15, 23, 42, .08); }
}
.wh-tab__badge { background: #7367f0; color: #fff; font-size: .62rem; padding: 1px 7px; border-radius: 999px; }
.wh-tool {
  width: 34px; height: 34px; border: none; border-radius: 10px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, .05); color: #64748b; font-size: .8rem;
  &:hover { background: rgba(37, 211, 102, .16); color: #1f9d57; }
  &:focus { outline: none; }
}
.wh-alert { margin: 0; border-radius: 12px; }
.wh-pending {
  display: flex; align-items: center; gap: 10px; background: rgba(37, 211, 102, .1); border: 1px solid rgba(37, 211, 102, .4);
  border-radius: 12px; padding: 8px 14px; font-size: .8rem; color: #1f6f45;
  i { color: #1f9d57; }
}
.wh-pending__x { border: none; background: none; color: #64748b; cursor: pointer; &:focus { outline: none; } }
.wh-fade-enter-active, .wh-fade-leave-active { transition: all .2s ease; }
.wh-fade-enter, .wh-fade-leave-to { opacity: 0; transform: translateY(-4px); }
.wh-panel {
  background: #fff; border: 1px solid rgba(15, 23, 42, .06); border-radius: 16px; padding: 16px 18px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, .04); min-height: 400px;
}
</style>

<style lang="scss">
/* webhook dialog (unscoped: rendered by b-modal outside this component) */
.wh-webhooks__title { display: inline-flex; align-items: center; gap: 8px; font-weight: 800; i { color: #25d366; } }
.wh-webhooks__body { padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 12px; }
.wh-webhooks__hint { margin: 0; font-size: .8rem; color: #64748b; line-height: 1.45; }
.wh-webhooks__item { display: flex; gap: 12px; align-items: flex-start; }
.wh-webhooks__step {
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; margin-top: 2px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(37, 211, 102, .16); color: #1f9d57; font-size: .74rem; font-weight: 800;
}
.wh-webhooks__main { flex: 1; min-width: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; }
.wh-webhooks__label { font-size: .8rem; font-weight: 700; color: #1f2a44; }
.wh-webhooks__method { font-size: .58rem; font-weight: 800; letter-spacing: .06em; padding: 2px 6px; border-radius: 5px; background: rgba(115, 103, 240, .12); color: #7367f0; }
.wh-webhooks__url {
  width: 100%; display: flex; align-items: center; gap: 6px;
  background: rgba(15, 23, 42, .04); border: 1px solid rgba(15, 23, 42, .08); border-radius: 10px; padding: 6px 6px 6px 12px;
  code { flex: 1; min-width: 0; font-size: .74rem; color: #1f2a44; background: none; padding: 0; word-break: break-all; }
  button {
    width: 30px; height: 30px; border: none; border-radius: 8px; flex-shrink: 0; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; background: #fff; color: #64748b; font-size: .8rem;
    box-shadow: 0 1px 2px rgba(15, 23, 42, .1); transition: all .15s;
    &:hover { background: rgba(37, 211, 102, .16); color: #1f9d57; }
    &:focus { outline: none; }
  }
}
.wh-webhooks__note { width: 100%; font-size: .68rem; color: #94a3b8; }
.wh-webhooks__where {
  display: flex; flex-direction: column; gap: 5px; font-size: .72rem; color: #475569;
  background: rgba(15, 23, 42, .04); border-radius: 10px; padding: 10px 12px; margin-top: 2px;
  b { font-size: .64rem; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; }
  span { display: flex; gap: 8px; align-items: flex-start; }
  i { color: #1f9d57; margin-top: 3px; flex-shrink: 0; }
}
.wh-webhooks__auto {
  display: flex; flex-direction: column; gap: 12px; border-radius: 14px; padding: 14px; border: 1px solid transparent;
  &[data-state="ok"] { background: rgba(40, 199, 111, .08); border-color: rgba(40, 199, 111, .35); .wh-webhooks__auto-icon { background: #28c76f; } }
  &[data-state="missing"] { background: rgba(255, 159, 67, .1); border-color: rgba(255, 159, 67, .4); .wh-webhooks__auto-icon { background: #ff9f43; } }
  &[data-state="unknown"] { background: rgba(100, 116, 139, .08); border-color: rgba(100, 116, 139, .25); .wh-webhooks__auto-icon { background: #94a3b8; } }
}
.wh-webhooks__auto-head { display: flex; align-items: center; gap: 12px; }
.wh-webhooks__auto-icon {
  width: 38px; height: 38px; border-radius: 12px; flex-shrink: 0; color: #fff; font-size: 1rem;
  display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(15, 23, 42, .15);
}
.wh-webhooks__auto-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; b { font-size: .84rem; color: #1f2a44; } small { font-size: .7rem; color: #64748b; line-height: 1.4; word-break: break-word; code { font-size: .66rem; background: none; padding: 0; color: #475569; } } }
.wh-webhooks__auto-btn { border-radius: 10px !important; font-weight: 700; padding: 9px 14px !important; box-shadow: 0 6px 14px rgba(40, 199, 111, .25); }
.wh-webhooks__again {
  align-self: flex-start; border: none; background: none; padding: 0; cursor: pointer;
  font-size: .72rem; font-weight: 700; color: #1f9d57;
  &:hover { text-decoration: underline; }
  &:focus { outline: none; }
  &:disabled { opacity: .6; cursor: default; }
}
/* settings dialog */
.wh-settings { padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 14px; }
.wh-settings__row { display: flex; align-items: center; gap: 14px; }
.wh-settings__text { flex: 1; display: flex; flex-direction: column; gap: 2px; b { font-size: .86rem; color: #1f2a44; } small { font-size: .72rem; color: #64748b; line-height: 1.4; } }
.wh-settings__switch { flex-shrink: 0; }
.wh-settings__block { display: flex; flex-direction: column; gap: 6px; }
.wh-settings__label { display: flex; align-items: center; font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; margin: 0; }
.wh-settings__hint { font-size: .68rem; color: #94a3b8; }
.wh-settings__foot { display: flex; justify-content: flex-end; gap: 8px; padding-top: 6px; border-top: 1px solid rgba(15, 23, 42, .06); }
.dark-layout {
  .wh-settings__text b { color: #e9ecf2; }
  .wh-settings__text small { color: #94a3b8; }
  .wh-settings__foot { border-color: rgba(255, 255, 255, .08); }
  .wh-webhooks__auto-text b { color: #e9ecf2; }
  .wh-webhooks__auto-text small, .wh-webhooks__auto-text code { color: #94a3b8; }
  .wh-webhooks__again { color: #4ade80; }
  .wh-webhooks__where { background: rgba(255, 255, 255, .05); }
  .wh-webhooks__hint { color: #94a3b8; }
  .wh-webhooks__label { color: #e9ecf2; }
  .wh-webhooks__url { background: rgba(255, 255, 255, .05); border-color: rgba(255, 255, 255, .1); code { color: #e9ecf2; } }
  .wh-webhooks__url button { background: #161d31; color: #94a3b8; box-shadow: none; }
  .wh-webhooks__where { color: #b4b7bd; }
  .wh-bar, .wh-panel { background: #283046; border-color: rgba(255, 255, 255, .07); box-shadow: 0 2px 10px rgba(0, 0, 0, .2); }
  .wh-brand__title { color: #e9ecf2; }
  .wh-branch-name { color: #b4b7bd; }
  .wh-tabs { background: rgba(255, 255, 255, .05); }
  .wh-tab { color: #94a3b8; &:hover { color: #e9ecf2; } &--on { background: #161d31; color: #4ade80; } }
  .wh-tool { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .wh-pending { color: #4ade80; }
}
</style>
